import Phaser from 'phaser';
import { StudioIntroScene } from './scenes/StudioIntroScene.js';
import { BootScene } from './scenes/BootScene.js';
import { MainMenuScene } from './scenes/MainMenuScene.js';
import { PrologueScene } from './scenes/PrologueScene.js';
import { Level1Scene } from './scenes/Level1Scene.js';
import { Level2Scene } from './scenes/Level2Scene.js';
import { MontageScene } from './scenes/MontageScene.js';
import { Level3Scene } from './scenes/Level3Scene.js';
import { CastleEscapeScene } from './scenes/CastleEscapeScene.js';
import { GardenEndingScene } from './scenes/GardenEndingScene.js';
import { dialogueManager } from './game/DialogueManager.js';
import { questManager } from './game/QuestManager.js';
import { audio } from './game/AudioManager.js';
import { mobileOrientationManager } from './game/MobileOrientationManager.js';
import { mobileFullscreenManager, requestMobileFullscreen, exitMobileFullscreen, isFullscreenActive } from './game/MobileFullscreenManager.js';

window.addEventListener('DOMContentLoaded', () => {
  const uiRoot = document.getElementById('ui-root');
  dialogueManager.mount(uiRoot);
  questManager.mount(uiRoot);
  mobileOrientationManager.init();
  mobileFullscreenManager.init();

  // Mobile / browser autoplay policy unlock on first user interaction
  const unlockAudio = () => {
    audio.init();
    if (audio.ctx && audio.ctx.state === 'suspended') {
      audio.ctx.resume().catch(() => {});
    }
  };
  ['touchstart', 'touchend', 'pointerdown', 'keydown', 'click'].forEach(evt => {
    window.addEventListener(evt, unlockAudio, { passive: true, once: true });
  });

  // Mobile gesture, double-tap zoom & text-selection protection
  document.addEventListener('gesturestart', (e) => e.preventDefault(), { passive: false });
  document.addEventListener('gesturechange', (e) => e.preventDefault(), { passive: false });
  document.addEventListener('gestureend', (e) => e.preventDefault(), { passive: false });
  document.addEventListener('dblclick', (e) => e.preventDefault(), { passive: false });
  document.addEventListener('contextmenu', (e) => {
    if (e.target && (e.target.closest('#app') || e.target.tagName === 'CANVAS' || e.target.closest('.touch-btn'))) {
      e.preventDefault();
    }
  });

  // Audio button toggle (#pause button in HUD)
  const audioToggle = document.getElementById('pause') || document.getElementById('btn-audio-toggle');
  if (audioToggle) {
    audioToggle.addEventListener('click', () => {
      const isMuted = audio.toggleMute();
      audioToggle.innerText = isMuted ? '🔇' : '🎵';
      audioToggle.title = isMuted ? 'Music: OFF (Click to Unmute)' : 'Music: ON (Click to Mute)';
    });
  }

  // Fullscreen button
  const fsBtn = document.getElementById('btn-fullscreen');
  if (fsBtn) {
    fsBtn.addEventListener('click', () => {
      if (!isFullscreenActive()) {
        const el = document.documentElement;
        const req = el.requestFullscreen || el.webkitRequestFullscreen || el.mozRequestFullScreen || el.msRequestFullscreen;
        if (req) {
          req.call(el).then(() => {
            if (screen.orientation && screen.orientation.lock) {
              screen.orientation.lock('landscape').catch(() => {});
            }
            window.dispatchEvent(new Event('mobile-layout-refresh'));
          }).catch(() => {});
        }
      } else {
        const exit = document.exitFullscreen || document.webkitExitFullscreen || document.mozCancelFullScreen || document.msExitFullscreen;
        if (exit) {
          exit.call(document).then(() => {
            window.dispatchEvent(new Event('mobile-layout-refresh'));
          }).catch(() => {});
        }
      }
    });
  }

  // Interactive Context Prompt tap handler for mobile
  const promptEl = document.getElementById('prompt');
  if (promptEl) {
    promptEl.style.cursor = 'pointer';
    promptEl.style.pointerEvents = 'auto';
    promptEl.addEventListener('click', (e) => {
      e.stopPropagation();
      window.dispatchEvent(new KeyboardEvent('keydown', { key: 'e', code: 'KeyE', bubbles: true }));
    });
  }

  const config = {
    type: Phaser.AUTO,
    parent: 'game-container',
    width: 960,
    height: 540,
    pixelArt: true,
    roundPixels: true,
    powerPreference: 'high-performance',
    render: {
      antialias: false,
      pixelArt: true,
      roundPixels: true
    },
    fps: {
      target: 60,
      min: 30,
      forceSetTimeOut: false
    },
    scale: {
      mode: Phaser.Scale.FIT,
      autoCenter: Phaser.Scale.CENTER_BOTH
    },
    input: {
      activePointers: 3,
      touch: {
        capture: false
      }
    },
    physics: {
      default: 'arcade',
      arcade: {
        gravity: { y: 0 },
        debug: false
      }
    },
    scene: [
      StudioIntroScene,
      BootScene,
      MainMenuScene,
      PrologueScene,
      Level1Scene,
      Level2Scene,
      MontageScene,
      Level3Scene,
      CastleEscapeScene,
      GardenEndingScene
    ]
  };

  const game = new Phaser.Game(config);
  window.__game = game;
});
